/**
 * Layer 2 - combining: what is one score for a group of notes?
 *
 * In order: the pile (notes, or slices), the scope (which part of the pile), the weighting (a
 * recency curve over "notes ago"), then input x statistic does the math. Levels nest within one
 * attempt: each level after the first takes the previous level's results as its pile, so a
 * rolling running score can feed a per-phrase minimum. Counting across attempts is policy.
 *
 * Per-hand and combined results are always both computed; which of them must pass is the
 * verdict's question.
 *
 * Scores are fractions (0 to 1), not percentages.
 */

import { JudgedTake, NoteJudgment, SliceJudgment } from './judgedTake';
import { Combiner } from './noteJudgment';
import { GradingConfigError, Hand } from './types';

/** A reading of credit as a named band (e.g. green / yellow / red). Display, not grading. */
export interface Band {
  readonly label: string;
  /** The band applies at or above this credit; bands are read top-down. */
  readonly minCredit: number;
}

export const DEFAULT_BANDS: readonly Band[] = [
  { label: 'green', minCredit: 1 },
  { label: 'yellow', minCredit: Number.MIN_VALUE },
  { label: 'red', minCredit: 0 },
];

export function bandOf(credit: number, bands: readonly Band[] = DEFAULT_BANDS): string {
  const band = bands.find((candidate) => credit >= candidate.minCredit);
  return band ? band.label : bands[bands.length - 1].label;
}

export type PileConfig =
  /** Each note is one item; extra notes that count as their own error are items too. */
  | { readonly unit: 'note'; readonly includePedal?: boolean }
  /**
   * Each onset slice is one item (lone notes are slices of one): the slice's own credit
   * combined with its members' credits. Missing members excused by an N-of-M presence rule
   * do not count against it.
   */
  | { readonly unit: 'slice'; readonly combiner: Combiner; readonly includePedal?: boolean };

export type Boundary =
  | { readonly by: 'take' }
  /** A group label from the score: phrase, measure, author region ... */
  | { readonly by: 'group'; readonly key: string }
  /** Segments between scope-reset markers (e.g. each loop pass). */
  | { readonly by: 'since-reset' };

export type Scope =
  /** The last N items, sliding forward: one running result after every item. */
  | { readonly kind: 'rolling'; readonly size: number; readonly per: 'items' | 'ms' }
  /**
   * A chunk with a boundary. Unreached notes inside it are counted as missed (partial play
   * earns partial credit against the whole scope) or excluded (only reached notes count).
   */
  | {
      readonly kind: 'bounded';
      readonly boundary: Boundary;
      readonly unreached: 'missed' | 'excluded';
    };

/** Weight over "notes ago" (0 = newest). Never rises with age; only mean and sum read it. */
export type RecencyCurve =
  | { readonly kind: 'flat' }
  /** Newest item weighs N (the pile size), the oldest 1: a straight-line fade. */
  | { readonly kind: 'rank' }
  /** Halves every `halfLife` items back. */
  | { readonly kind: 'exponential'; readonly halfLife: number }
  /** Explicit weights, newest first; the last weight repeats. */
  | { readonly kind: 'points'; readonly weights: readonly number[] };

export type ItemInput =
  /** The item's credit. */
  | { readonly kind: 'credit' }
  /** 1 - credit (error count with `sum`, error rate with `mean`). */
  | { readonly kind: 'credit-lost' }
  /** 1 when the credit is at or above the target, else 0. */
  | { readonly kind: 'indicator'; readonly atLeast: number }
  /** A weight per band, e.g. red 1, yellow 0.5: weighted errors. */
  | {
      readonly kind: 'band-weight';
      readonly weights: Readonly<Record<string, number>>;
      readonly bands?: readonly Band[];
    }
  /** A count per band (needs `sum`). */
  | { readonly kind: 'tally'; readonly bands?: readonly Band[] };

/**
 * - `mean`, `sum`: weighted by the recency curve.
 * - `minimum`: the worst value in the scope.
 * - `longest-run`: the longest run of indicator 1s.
 * - `final`: the value of the last item in the scope - for a bounded scope, the value it ended
 *   on (e.g. a running score read where a phrase ends).
 */
export type Statistic = 'mean' | 'sum' | 'minimum' | 'longest-run' | 'final';

export interface CombiningLevel {
  readonly scope: Scope;
  readonly recency?: RecencyCurve;
  readonly input: ItemInput;
  readonly statistic: Statistic;
}

export interface CombiningConfig {
  readonly pile: PileConfig;
  /** Innermost first. Every level after the first reads the previous level's results. */
  readonly levels: readonly CombiningLevel[];
}

export type ViewName = 'combined' | Hand;

export const VIEWS: readonly ViewName[] = ['combined', 'left', 'right'];

/** One result of a level: a running value after an item, or one bounded group's value. */
export interface ScorePoint {
  /** Null when the pile was empty. */
  readonly value: number | null;
  /** Band counts, for `tally` input. */
  readonly tally: Readonly<Record<string, number>> | null;
  /** The bounded group this point belongs to ('take' for the whole take), or null for rolling. */
  readonly group: string | null;
  /** Items that went into it. */
  readonly count: number;
  /** Labels of the last item, so an outer level can group running values. */
  readonly groups: Readonly<Record<string, string>>;
  readonly position: number;
  readonly timeMs: number | null;
}

export interface ViewScore {
  readonly view: ViewName;
  /** The outermost level's points. */
  readonly points: readonly ScorePoint[];
  /** The latest point (the running value now, or the single bounded value). */
  readonly latest: ScorePoint | null;
}

export type ViewScores = Readonly<Record<ViewName, ViewScore>>;

// ---------------------------------------------------------------------------------------------
// Validation.

function recencyErrors(recency: RecencyCurve | undefined): string[] {
  if (recency === undefined || recency.kind === 'flat' || recency.kind === 'rank') {
    return [];
  }
  if (recency.kind === 'exponential') {
    return recency.halfLife > 0 ? [] : ['recency: half-life must be positive'];
  }
  const weights = recency.weights;
  const badValue = weights.some((weight) => !(weight >= 0));
  const rises = weights.some((weight, index) => index > 0 && weight > weights[index - 1]);
  return [
    ...(weights.length === 0 || badValue
      ? ['recency: weights must be a non-empty list of numbers >= 0']
      : []),
    ...(rises ? ['recency: a weight never rises with age'] : []),
  ];
}

export function combiningErrors(config: CombiningConfig): string[] {
  const levelErrors = config.levels.reduce<string[]>((errors, level, index) => {
    const where = `level ${index}`;
    const recencyWithoutMath =
      level.recency &&
      level.recency.kind !== 'flat' &&
      level.statistic !== 'mean' &&
      level.statistic !== 'sum'
        ? [`${where}: a recency curve applies only to mean and sum`]
        : [];
    const tallyMath =
      level.input.kind === 'tally' && level.statistic !== 'sum'
        ? [`${where}: a tally needs the sum statistic`]
        : [];
    const runMath =
      level.statistic === 'longest-run' && level.input.kind !== 'indicator'
        ? [`${where}: longest run needs indicator input`]
        : [];
    const tallyInner =
      level.input.kind === 'tally' && index < config.levels.length - 1
        ? [`${where}: a tally can only be the outermost level`]
        : [];
    const rollingSize =
      level.scope.kind === 'rolling' && !(level.scope.size > 0)
        ? [`${where}: a rolling scope needs a positive size`]
        : [];
    return [
      ...errors,
      ...recencyErrors(level.recency).map((error) => `${where}: ${error}`),
      ...recencyWithoutMath,
      ...tallyMath,
      ...runMath,
      ...tallyInner,
      ...rollingSize,
    ];
  }, []);
  return [
    ...(config.levels.length === 0 ? ['combining needs at least one level'] : []),
    ...levelErrors,
  ];
}

export function validateCombining(config: CombiningConfig): void {
  const errors = combiningErrors(config);
  if (errors.length > 0) {
    throw new GradingConfigError(`combining: ${errors.join('; ')}`);
  }
}

// ---------------------------------------------------------------------------------------------
// The pile.

/** One thing to be counted: a note, an extra note, a slice or a pedal mark. */
export interface PileItem {
  readonly id: string;
  readonly kind: 'note' | 'extra' | 'slice' | 'pedal';
  readonly hands: readonly Hand[];
  /** Score position in beats; orders the pile. */
  readonly position: number;
  /** When it was played (or came due); null when unreached. */
  readonly timeMs: number | null;
  /** Null when the item is not reached yet (scope decides whether that counts as missed). */
  readonly credit: number | null;
  readonly groups: Readonly<Record<string, string>>;
}

function isReached(judgment: NoteJudgment): boolean {
  return judgment.status === 'matched' || judgment.status === 'missed';
}

function noteItem(judgment: NoteJudgment): PileItem {
  return {
    id: judgment.noteId,
    kind: 'note',
    hands: [judgment.hand],
    position: judgment.onsetBeat,
    timeMs: judgment.press ? judgment.press.timeMs : isReached(judgment) ? judgment.dueMs : null,
    credit: isReached(judgment) ? (judgment.credit as number) : null,
    groups: judgment.groups,
  };
}

function extraItems(take: JudgedTake, byId: ReadonlyMap<string, NoteJudgment>): PileItem[] {
  return take.extras
    .filter((extra) => extra.countsAs === 'own-error' && extra.chargedTo !== null)
    .map((extra, index) => {
      const charged = byId.get(extra.chargedTo as string) as NoteJudgment;
      return {
        id: `extra:${index}`,
        kind: 'extra' as const,
        hands: [charged.hand],
        position: charged.onsetBeat,
        timeMs: extra.press.timeMs,
        credit: 0,
        groups: charged.groups,
      };
    });
}

function combineCredits(combiner: Combiner, credits: readonly number[]): number {
  if (credits.length === 0) {
    return 0;
  }
  if (combiner === 'worst-of') {
    return Math.min(...credits);
  }
  if (combiner === 'product') {
    return credits.reduce((product, credit) => product * credit, 1);
  }
  return credits.reduce((sum, credit) => sum + credit, 0) / credits.length;
}

function sliceItem(
  slice: SliceJudgment,
  members: readonly NoteJudgment[],
  combiner: Combiner,
  view: ViewName,
): PileItem | null {
  const inView = view === 'combined' ? members : members.filter((member) => member.hand === view);
  if (inView.length === 0) {
    return null;
  }
  const reached = inView.filter(isReached);
  const first = inView[0];
  const base = {
    id: slice.id,
    kind: 'slice' as const,
    hands: slice.hands,
    position: first.onsetBeat,
    groups: first.groups,
  };
  if (reached.length === 0) {
    return { ...base, timeMs: null, credit: null };
  }
  const timeMs = Math.min(
    ...reached.map((member) => (member.press ? member.press.timeMs : (member.dueMs as number))),
  );
  // A slice crossing hands is judged as a whole only in the combined view; a hand's view of it
  // is that hand's members alone.
  const wholeSlice = view === 'combined' || slice.hands.length === 1;
  if (wholeSlice && slice.credit !== null) {
    const counted =
      slice.presencePassed === true && slice.present < slice.total
        ? reached.filter((member) => member.status === 'matched')
        : reached;
    const credit =
      slice.credit === 0
        ? 0
        : combineCredits(combiner, [
            ...counted.map((member) => member.credit as number),
            slice.credit,
          ]);
    return { ...base, timeMs, credit };
  }
  return {
    ...base,
    timeMs,
    credit: combineCredits(
      combiner,
      reached.map((member) => member.credit as number),
    ),
  };
}

/** The items a view counts, in score order. */
export function buildPile(take: JudgedTake, pile: PileConfig, view: ViewName): PileItem[] {
  const graded = take.notes.filter((judgment) => judgment.status !== 'not-graded');
  const byId = new Map(graded.map((judgment) => [judgment.noteId, judgment] as const));
  const inView = (item: PileItem): boolean => view === 'combined' || item.hands.indexOf(view) >= 0;
  const pedalItems: PileItem[] =
    pile.includePedal && view === 'combined'
      ? take.pedal.map((judgment) => ({
          id: `pedal:${judgment.markId}`,
          kind: 'pedal' as const,
          hands: [],
          position: judgment.downBeat,
          timeMs: judgment.timeMs,
          credit:
            judgment.status === 'matched' || judgment.status === 'missed'
              ? (judgment.credit as number)
              : null,
          groups: {},
        }))
      : [];
  const extras = extraItems(take, byId).filter(inView);
  if (pile.unit === 'note') {
    const notes = graded.map(noteItem).filter(inView);
    return orderPile([...notes, ...extras, ...pedalItems]);
  }
  const sliced = take.onsetSlices.reduce<Set<string>>((ids, slice) => {
    slice.memberIds.forEach((id) => ids.add(id));
    return ids;
  }, new Set<string>());
  const slices = take.onsetSlices
    .map((slice) => {
      const members = slice.memberIds
        .map((id) => byId.get(id))
        .filter((member): member is NoteJudgment => member !== undefined);
      return sliceItem(slice, members, pile.combiner, view);
    })
    .filter((item): item is PileItem => item !== null);
  const loneNotes = graded
    .filter((judgment) => !sliced.has(judgment.noteId))
    .map(noteItem)
    .filter(inView);
  return orderPile([...slices, ...loneNotes, ...extras, ...pedalItems]);
}

const KIND_ORDER: Readonly<Record<PileItem['kind'], number>> = {
  note: 0,
  slice: 0,
  extra: 1,
  pedal: 2,
};

function orderPile(items: readonly PileItem[]): PileItem[] {
  return items
    .map((item, sequence) => ({ item, sequence }))
    .sort(
      (a, b) =>
        a.item.position - b.item.position ||
        KIND_ORDER[a.item.kind] - KIND_ORDER[b.item.kind] ||
        (a.item.timeMs ?? 0) - (b.item.timeMs ?? 0) ||
        a.sequence - b.sequence,
    )
    .map((entry) => entry.item);
}

// ---------------------------------------------------------------------------------------------
// The math.

/** A value with the labels needed to group and order it. Items and inner results both are. */
interface Entry {
  readonly value: number | null;
  readonly credit: number | null;
  readonly groups: Readonly<Record<string, string>>;
  readonly position: number;
  readonly timeMs: number | null;
}

export function recencyWeight(
  recency: RecencyCurve | undefined,
  notesAgo: number,
  pileSize: number,
): number {
  if (recency === undefined || recency.kind === 'flat') {
    return 1;
  }
  if (recency.kind === 'rank') {
    return pileSize - notesAgo;
  }
  if (recency.kind === 'exponential') {
    return Math.pow(0.5, notesAgo / recency.halfLife);
  }
  return recency.weights[Math.min(notesAgo, recency.weights.length - 1)];
}

function inputValue(input: ItemInput, credit: number): number {
  switch (input.kind) {
    case 'credit':
      return credit;
    case 'credit-lost':
      return 1 - credit;
    case 'indicator':
      return credit >= input.atLeast ? 1 : 0;
    case 'band-weight':
      return input.weights[bandOf(credit, input.bands)] ?? 0;
    case 'tally':
      return 0;
  }
}

function longestRun(values: readonly number[]): number {
  return values.reduce<{ best: number; current: number }>(
    (run, value) => {
      const current = value === 1 ? run.current + 1 : 0;
      return { best: Math.max(run.best, current), current };
    },
    { best: 0, current: 0 },
  ).best;
}

function computeValue(level: CombiningLevel, credits: readonly number[]): number | null {
  if (credits.length === 0) {
    return null;
  }
  const values = credits.map((credit) => inputValue(level.input, credit));
  const weights = values.map((_, index) =>
    recencyWeight(level.recency, values.length - 1 - index, values.length),
  );
  switch (level.statistic) {
    case 'mean': {
      const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
      return totalWeight === 0
        ? null
        : values.reduce((sum, value, index) => sum + value * weights[index], 0) / totalWeight;
    }
    case 'sum':
      return values.reduce((sum, value, index) => sum + value * weights[index], 0);
    case 'minimum':
      return Math.min(...values);
    case 'longest-run':
      return longestRun(values);
    case 'final':
      return values[values.length - 1];
  }
}

function computeTally(
  level: CombiningLevel,
  credits: readonly number[],
): Record<string, number> | null {
  if (level.input.kind !== 'tally') {
    return null;
  }
  const bands = level.input.bands ?? DEFAULT_BANDS;
  const empty = bands.reduce<Record<string, number>>(
    (counts, band) => ({ ...counts, [band.label]: 0 }),
    {},
  );
  return credits.reduce<Record<string, number>>((counts, credit) => {
    const label = bandOf(credit, bands);
    return { ...counts, [label]: (counts[label] ?? 0) + 1 };
  }, empty);
}

function point(level: CombiningLevel, pile: readonly Entry[], group: string | null): ScorePoint {
  const credits = pile.map((entry) => entry.credit as number);
  const last = pile.length > 0 ? pile[pile.length - 1] : null;
  return {
    value: level.input.kind === 'tally' ? null : computeValue(level, credits),
    tally: computeTally(level, credits),
    group,
    count: pile.length,
    groups: last ? last.groups : {},
    position: last ? last.position : 0,
    timeMs: last ? last.timeMs : null,
  };
}

function segmentOf(timeMs: number | null, resets: readonly number[]): number {
  if (timeMs === null) {
    return resets.length;
  }
  return resets.filter((resetMs) => resetMs <= timeMs).length;
}

function boundedGroupKey(
  boundary: Boundary,
  entry: Entry,
  resets: readonly number[],
): string | null {
  if (boundary.by === 'take') {
    return 'take';
  }
  if (boundary.by === 'group') {
    const label = entry.groups[boundary.key];
    return label === undefined ? null : label;
  }
  return `segment:${segmentOf(entry.timeMs, resets)}`;
}

function runLevel(
  level: CombiningLevel,
  entries: readonly Entry[],
  resets: readonly number[],
): ScorePoint[] {
  const scope = level.scope;
  if (scope.kind === 'rolling') {
    const reached = entries.filter((entry) => entry.credit !== null);
    return reached.map((entry, index) => {
      const window =
        scope.per === 'items'
          ? reached.slice(Math.max(0, index + 1 - scope.size), index + 1)
          : reached
              .slice(0, index + 1)
              .filter(
                (candidate) =>
                  candidate.timeMs !== null &&
                  entry.timeMs !== null &&
                  candidate.timeMs > entry.timeMs - scope.size,
              );
      return point(level, window, null);
    });
  }
  const counted = entries
    .filter((entry) => entry.credit !== null || scope.unreached === 'missed')
    .map((entry) => (entry.credit === null ? { ...entry, credit: 0 } : entry));
  const groups = new Map<string, Entry[]>();
  counted.forEach((entry) => {
    const key = boundedGroupKey(scope.boundary, entry, resets);
    if (key !== null) {
      groups.set(key, [...(groups.get(key) ?? []), entry]);
    }
  });
  if (scope.boundary.by === 'take' && groups.size === 0) {
    return [point(level, [], 'take')];
  }
  return Array.from(groups.entries()).map(([key, pile]) => point(level, pile, key));
}

function toEntries(points: readonly ScorePoint[]): Entry[] {
  return points.map((scored) => ({
    value: scored.value,
    credit: scored.value,
    groups: scored.groups,
    position: scored.position,
    timeMs: scored.timeMs,
  }));
}

/** The same combining with its outermost level read by another statistic. */
export function withOutermostStatistic(
  config: CombiningConfig,
  statistic: Statistic,
): CombiningConfig {
  const outermost = config.levels.length - 1;
  return {
    ...config,
    levels: config.levels.map((level, index) =>
      index === outermost ? { ...level, statistic } : level,
    ),
  };
}

/** Runs the combining levels over one view of the take. */
export function scoreView(take: JudgedTake, config: CombiningConfig, view: ViewName): ViewScore {
  const items = buildPile(take, config.pile, view);
  const firstEntries: Entry[] = items.map((item) => ({
    value: item.credit,
    credit: item.credit,
    groups: item.groups,
    position: item.position,
    timeMs: item.timeMs,
  }));
  const points = config.levels.reduce<ScorePoint[] | null>((previous, level) => {
    const entries =
      previous === null
        ? firstEntries
        : toEntries(previous).filter((entry) => entry.credit !== null);
    return runLevel(level, entries, take.scopeResetsMs);
  }, null) as ScorePoint[];
  return { view, points, latest: points.length > 0 ? points[points.length - 1] : null };
}

/** Combined, left-hand and right-hand scores, always all three. */
export function scoreViews(take: JudgedTake, config: CombiningConfig): ViewScores {
  validateCombining(config);
  return {
    combined: scoreView(take, config, 'combined'),
    left: scoreView(take, config, 'left'),
    right: scoreView(take, config, 'right'),
  };
}
